import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Activity, 
  CheckCircle,
  AlertTriangle,
  XCircle,
  Play,
  Pause,
  RefreshCw,
  Database,
  Server,
  Wifi,
  Cpu,
  HardDrive,
  MemoryStick,
  Monitor,
  MessageCircle,
  Terminal,
  Bug,
  Zap,
  Clock,
  TrendingUp,
  BarChart3,
  Settings
} from 'lucide-react';
import { useAuth0Context } from '../contexts/Auth0Context';
import { PROCOGIA_COLORS } from '../utils/themeUtils';

const SystemTestingDashboard = () => {
  const { appUser } = useAuth0Context();
  const [systemHealth, setSystemHealth] = useState({});
  const [testResults, setTestResults] = useState([]);
  const [activeTests, setActiveTests] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSystemData();
    startRealTimeMonitoring();
    
    return () => {
      // Cleanup real-time monitoring
    };
  }, []);

  const fetchSystemData = async () => {
    try {
      // Simulate comprehensive system data
      setSystemHealth({
        api: { status: 'healthy', responseTime: 145, uptime: 99.7 },
        database: { status: 'healthy', connections: 12, queryTime: 23 },
        redis: { status: 'healthy', memory: 67, hitRate: 94.2 },
        auth: { status: 'healthy', tokenValidation: 98 },
        ml: { status: 'warning', modelAccuracy: 92.1, lastTrained: '2 hours ago' },
        network: { status: 'healthy', latency: 12, bandwidth: 85 },
        server: { 
          cpu: 34.2, 
          memory: 67.8, 
          disk: 45.3, 
          load: 1.2,
          uptime: '15 days, 4 hours'
        }
      });

      setTestResults([
        {
          id: 1,
          name: 'API Endpoint Tests',
          type: 'api',
          status: 'passed',
          duration: 2.34,
          timestamp: new Date(Date.now() - 300000),
          details: 'All 24 endpoints responding correctly'
        },
        {
          id: 2,
          name: 'Database Connectivity',
          type: 'database',
          status: 'passed',
          duration: 0.87,
          timestamp: new Date(Date.now() - 600000),
          details: 'Connection pool healthy, queries executing normally'
        },
        {
          id: 3,
          name: 'ML Model Performance',
          type: 'ml',
          status: 'warning',
          duration: 15.23,
          timestamp: new Date(Date.now() - 900000),
          details: 'Accuracy dropped to 92.1%, consider retraining'
        },
        {
          id: 4,
          name: 'Auth0 Integration',
          type: 'auth',
          status: 'passed',
          duration: 1.45,
          timestamp: new Date(Date.now() - 1200000),
          details: 'Token validation and user sync working correctly'
        },
        {
          id: 5,
          name: 'Real-time Streaming',
          type: 'streaming',
          status: 'failed',
          duration: 8.91,
          timestamp: new Date(Date.now() - 1500000),
          details: 'WebSocket connection intermittent, requires investigation'
        }
      ]);

      setActiveTests([
        { name: 'Load Testing', progress: 67, eta: '2 min remaining' },
        { name: 'Security Scan', progress: 23, eta: '8 min remaining' }
      ]);

      setLogs([
        { level: 'info', message: 'System health check completed successfully', timestamp: new Date(Date.now() - 180000) },
        { level: 'warning', message: 'ML model accuracy below threshold (92.1%)', timestamp: new Date(Date.now() - 240000) },
        { level: 'error', message: 'WebSocket connection failed for equipment EQ-045', timestamp: new Date(Date.now() - 360000) },
        { level: 'info', message: 'Database cleanup completed, 1,247 old records archived', timestamp: new Date(Date.now() - 480000) },
        { level: 'info', message: 'Auth0 token refresh successful for 98 users', timestamp: new Date(Date.now() - 600000) }
      ]);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching system data:', error);
      setLoading(false);
    }
  };

  const startRealTimeMonitoring = () => {
    // Simulate real-time updates
    const interval = setInterval(() => {
      setSystemHealth(prev => ({
        ...prev,
        server: {
          ...prev.server,
          cpu: Math.random() * 40 + 20,
          memory: Math.random() * 20 + 60,
          load: Math.random() * 2 + 0.5
        }
      }));
    }, 3000);

    return () => clearInterval(interval);
  };

  const StatusCard = ({ icon, title, status, value, subtitle, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`flex-shrink-0 p-2 rounded-lg bg-${color}-100`}>
            <div className={`text-${color}-600`}>{icon}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">{title}</div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                status === 'healthy' ? 'bg-green-500' :
                status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
              }`}></div>
              <span className="text-lg font-bold text-gray-900">{value}</span>
            </div>
            {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
          </div>
        </div>
      </div>
    </div>
  );

  const TestResultCard = ({ test }) => {
    const getStatusIcon = (status) => {
      switch (status) {
        case 'passed': return <CheckCircle className="w-4 h-4 text-green-600" />;
        case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
        case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
        default: return <Activity className="w-4 h-4 text-gray-600" />;
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {getStatusIcon(test.status)}
            <span className="font-medium text-gray-900">{test.name}</span>
          </div>
          <span className="text-sm text-gray-500">{test.duration}s</span>
        </div>
        <p className="text-sm text-gray-600 mb-2">{test.details}</p>
        <div className="text-xs text-gray-400">
          {test.timestamp.toLocaleString()}
        </div>
      </div>
    );
  };

  const SystemMetrics = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Real-Time Metrics</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Cpu className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-gray-700">CPU Usage</span>
          </div>
          <div className="flex items-center space-x-2 flex-1 mx-4">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${systemHealth.server?.cpu || 0}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600 w-12">{systemHealth.server?.cpu?.toFixed(1) || 0}%</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MemoryStick className="w-4 h-4 text-green-600" />
            <span className="text-sm text-gray-700">Memory</span>
          </div>
          <div className="flex items-center space-x-2 flex-1 mx-4">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ width: `${systemHealth.server?.memory || 0}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600 w-12">{systemHealth.server?.memory?.toFixed(1) || 0}%</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <HardDrive className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-gray-700">Disk Usage</span>
          </div>
          <div className="flex items-center space-x-2 flex-1 mx-4">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full" 
                style={{ width: `${systemHealth.server?.disk || 0}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600 w-12">{systemHealth.server?.disk?.toFixed(1) || 0}%</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="w-4 h-4 text-orange-600" />
            <span className="text-sm text-gray-700">Load Average</span>
          </div>
          <span className="text-sm text-gray-600">{systemHealth.server?.load?.toFixed(2) || 0}</span>
        </div>
      </div>
    </div>
  );

  const LogsPanel = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">System Logs</h3>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {logs.map((log, index) => (
          <div key={index} className={`flex items-start space-x-3 p-2 rounded ${
            log.level === 'error' ? 'bg-red-50' :
            log.level === 'warning' ? 'bg-yellow-50' : 'bg-gray-50'
          }`}>
            <div className={`w-2 h-2 rounded-full mt-2 ${
              log.level === 'error' ? 'bg-red-500' :
              log.level === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
            }`}></div>
            <div className="flex-1">
              <div className="text-sm text-gray-900">{log.message}</div>
              <div className="text-xs text-gray-500">{log.timestamp.toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Testing & Monitoring</h1>
          <p className="text-gray-600">Monitor system health and run diagnostic tests</p>
        </div>
        <div className="flex items-center space-x-3">

          <button 
            onClick={fetchSystemData}
            className="flex items-center space-x-2 text-white px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: PROCOGIA_COLORS.blue
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = PROCOGIA_COLORS.green;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = PROCOGIA_COLORS.blue;
            }}
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatusCard
          icon={<Server className="w-5 h-5" />}
          title="API Server"
          status={systemHealth.api?.status}
          value={`${systemHealth.api?.responseTime || 0}ms`}
          subtitle={`${systemHealth.api?.uptime || 0}% uptime`}
          color="blue"
        />
        <StatusCard
          icon={<Database className="w-5 h-5" />}
          title="Database"
          status={systemHealth.database?.status}
          value={`${systemHealth.database?.connections || 0} conn`}
          subtitle={`${systemHealth.database?.queryTime || 0}ms avg`}
          color="green"
        />
        <StatusCard
          icon={<Zap className="w-5 h-5" />}
          title="Redis Cache"
          status={systemHealth.redis?.status}
          value={`${systemHealth.redis?.hitRate || 0}%`}
          subtitle={`${systemHealth.redis?.memory || 0}% memory`}
          color="red"
        />
        <StatusCard
          icon={<Shield className="w-5 h-5" />}
          title="Authentication"
          status={systemHealth.auth?.status}
          value={`${systemHealth.auth?.tokenValidation || 0}%`}
          subtitle="Token validation rate"
          color="purple"
        />
      </div>

      {/* Additional Services */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatusCard
          icon={<Monitor className="w-5 h-5" />}
          title="ML Model"
          status={systemHealth.ml?.status}
          value={`${systemHealth.ml?.modelAccuracy || 0}%`}
          subtitle={`Trained ${systemHealth.ml?.lastTrained || 'unknown'}`}
          color="yellow"
        />
        <StatusCard
          icon={<Wifi className="w-5 h-5" />}
          title="Network"
          status={systemHealth.network?.status}
          value={`${systemHealth.network?.latency || 0}ms`}
          subtitle={`${systemHealth.network?.bandwidth || 0}% bandwidth`}
          color="indigo"
        />
        <StatusCard
          icon={<Activity className="w-5 h-5" />}
          title="System Uptime"
          status="healthy"
          value={systemHealth.server?.uptime || 'Unknown'}
          subtitle="Current session"
          color="pink"
        />
      </div>

      {/* Active Tests */}
      {activeTests.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Running Tests</h3>
          <div className="space-y-4">
            {activeTests.map((test, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{test.name}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${test.progress}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{test.progress}%</span>
                  </div>
                  <span className="text-xs text-gray-500">{test.eta}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SystemMetrics />
        <LogsPanel />
      </div>

      {/* Test Results */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Test Results</h3>
          <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
            <Play className="w-4 h-4" />
            <span>Run All Tests</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testResults.map((test) => (
            <TestResultCard key={test.id} test={test} />
          ))}
        </div>
      </div>


    </div>
  );
};

export default SystemTestingDashboard; 